import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

// Masked line-by-line reveal. onLoad = animate immediately, otherwise on scroll.
export const Lines = ({ lines, as = "h2", className = "", delay = 0, stagger = 0.11, onLoad = false, lineClass = "" }) => {
  const Tag = motion[as];
  const trigger = onLoad ? { animate: "show" } : { whileInView: "show", viewport: { once: true, amount: 0.1 } };
  return (
    <Tag className={className} initial="hide" {...trigger}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block ${lineClass}`}
            variants={{ hide: { y: "108%" }, show: { y: "0%" } }}
            transition={{ duration: 1.15, ease: EASE, delay: delay + i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// Clip-path reveal + gentle parallax. Observer sits on the unclipped wrapper.
export const Frame = ({ src, alt, className = "", imgClass = "", parallax = 8, delay = 0, priority = false, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${parallax}%`, `${parallax}%`]);
  return (
    <motion.div ref={ref} className={`relative ${className}`} initial="hide" whileInView="show" viewport={{ once: true, amount: 0.1 }} data-testid={testid}>
      <motion.div
        className="absolute inset-0 overflow-hidden bg-ink/10"
        variants={{ hide: { clipPath: "inset(100% 0% 0% 0%)" }, show: { clipPath: "inset(0% 0% 0% 0%)" } }}
        transition={{ duration: 1.3, ease: EASE, delay }}
      >
        <motion.img
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          style={{ y }}
          className={`photo absolute inset-0 h-full w-full scale-[1.18] object-cover ${imgClass}`}
        />
      </motion.div>
    </motion.div>
  );
};
